import { US_STATES } from "@/shared/constants/constants";
import { splitFullNameParts } from "@/shared/utils/names";

import type { TrustExtraction } from "@/shared/components/document-viewer/TrustInsights";

export interface TrustValidationWarning {
  message: string;
  severity: "warning" | "error" | "info";
  section: string;
}

const REVOCABLE_TYPES = new Set(["REVOCABLE_LIVING", "Revocable", "Revocable Living Trust"]);

function canonicalizeState(state: string): string {
  const cleaned = state.replace(/^state of\s+/i, "").replace(/\./g, "").toLowerCase().trim();
  const byLabel = US_STATES.find((s) => s.label.toLowerCase() === cleaned);
  if (byLabel) return byLabel.value;
  const byValue = US_STATES.find((s) => s.value.toLowerCase() === cleaned);
  if (byValue) return byValue.value;
  return cleaned.toUpperCase();
}

export function validateTrust(
  data: TrustExtraction,
  decedentName?: string | null,
  _estateState?: string | null,
  dateOfDeath?: string | null,
): TrustValidationWarning[] {
  const warnings: TrustValidationWarning[] = [];
  const info = data.trust_information;
  const grantor = info?.grantor_name_as_printed ?? info?.grantor_name ?? null;

  if (grantor && decedentName) {
    const grantorParts = splitFullNameParts(grantor);
    const decedentParts = splitFullNameParts(decedentName);
    const normalize = (s: string) => s.toLowerCase().trim();
    if (
      normalize(grantorParts.firstName) !== normalize(decedentParts.firstName) ||
      normalize(grantorParts.lastName) !== normalize(decedentParts.lastName)
    ) {
      warnings.push({
        message: `Grantor name "${grantor}" does not match decedent name "${decedentName}". Verify a/k/a names.`,
        severity: "warning",
        section: "trust_information",
      });
    }
  }

  if (!!dateOfDeath && info?.trust_type && REVOCABLE_TYPES.has(info.trust_type) && !info?.trust_ein) {
    warnings.push({
      message:
        "This revocable living trust will need its own EIN now that the grantor has passed.",
      severity: "warning",
      section: "trust_information",
    });
  }

  if (!!dateOfDeath && data.sub_trusts?.length) {
    for (const sub of data.sub_trusts) {
      if (REVOCABLE_TYPES.has(info?.trust_type ?? "")) {
        warnings.push({
          message: `Sub-trust "${sub.name || "Unnamed"}" will need its own EIN.`,
          severity: "info",
          section: "sub_trusts",
        });
      }
    }
  }

  const govLaw = info?.governing_law_state ?? info?.state_created ?? null;
  if (govLaw && canonicalizeState(govLaw) !== "NY") {
    warnings.push({
      message: `Trust governed by ${govLaw} law. Multi-state considerations may apply.`,
      severity: "info",
      section: "trust_information",
    });
  }

  if (data.pour_over_will?.has_pour_over_will) {
    warnings.push({
      message:
        "This trust references a pour-over will. Verify the will also references this trust.",
      severity: "info",
      section: "pour_over_will",
    });
  }

  if (data.amendments?.length) {
    const hasRestatement = data.amendments.some((a) => a.amendment_scope === "RESTATEMENT");
    if (hasRestatement) {
      warnings.push({
        message:
          "A restatement was detected. Verify that this document reflects the most current version of the trust.",
        severity: "warning",
        section: "amendments",
      });
    }
  }

  return warnings;
}
